/*
 * @Author: Ryan
 * @Date: 2026-01-26
 * @Description: Why choose FalcoCut AI Image Generator section with 6 feature cards
 */
import React from 'react';

interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

const features: FeatureCard[] = [
  {
    icon: '⚡️',
    title: 'Fast Processing',
    description:
      'Images generate in seconds, keeping projects moving without long waits. Rapid turnaround makes it easy to test ideas, explore styles, and stay in a creative flow.',
  },
  {
    icon: '🌐',
    title: 'High-Quality Output',
    description:
      'Each result comes through with sharp details and clean, balanced color. Visuals look polished enough for marketing, design, and social content without extra editing.',
  },
  {
    icon: '🧩',
    title: 'Advanced AI Models',
    description:
      'Modern AI models interpret prompts with greater accuracy, producing clearer subjects, better shapes, and more realistic textures.',
  },
  {
    icon: '🔒',
    title: 'Secure and Private by Design',
    description:
      'Your images and files stay protected in a secure cloud environment. All projects remain private while you work across devices.',
  },
  {
    icon: '☁️',
    title: 'Cloud-Based Access',
    description:
      'Everything runs online with no installation required. Work continues seamlessly across devices, supporting flexible schedules and mobile-first creators.',
  },
  {
    icon: '🧑‍💻',
    title: 'Beginner-Friendly Interface',
    description:
      'A clean, beginner-friendly interface keeps the workflow simple from the first prompt. Anyone can generate or refine visuals without a learning curve or technical setup.',
  },
];

const FeatureCardItem: React.FC<FeatureCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose FalcoCut AI Image Generator
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Our AI image generator is built to help anyone create high-quality visuals with
          ease. It combines speed, clarity, and smart technology so you can focus on your
          ideas instead of complex tools. Whether you are a beginner or a pro, the
          workflow stays simple and smooth from start to finish.
        </p>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6">
          {features.map((feature, index) => (
            <FeatureCardItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
